import React, { useEffect, useState } from "react";
import "./App.css";
import { db } from "./firebase";
import { collection, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { storage, auth } from "./firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  setPersistence,
  browserSessionPersistence,
} from "firebase/auth";

import EODLogger from "./EODLogger";
import Reports from "./Reports";
import Attach from "./Attach";
import Charts from "./Charts";
import Admin from "./Admin";

export default function App() {
  const SHARED_LOGIN_EMAIL = "storelogin@internal-app.com";

  const [authReady, setAuthReady] = useState(false);
  const [siteUnlocked, setSiteUnlocked] = useState(false);
  const [sitePassword, setSitePassword] = useState("");
  const [siteAuthLoading, setSiteAuthLoading] = useState(false);
  const [siteAuthError, setSiteAuthError] = useState("");

  const [activeTab, setActiveTab] = useState("EOD");
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminError, setAdminError] = useState("");

  const [reps, setReps] = useState([]);
  const [repProfilesMap, setRepProfilesMap] = useState({});
  const [loadingReps, setLoadingReps] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [draftProfileName, setDraftProfileName] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  const [profilePhotoURL, setProfilePhotoURL] = useState("");
  const [profilePhotoPath, setProfilePhotoPath] = useState("");
  const [profileUploading, setProfileUploading] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setSiteUnlocked(!!user);
      setAuthReady(true);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!siteUnlocked) return;

    const fetchInitialData = async () => {
      try {
        const [repsSnap, repProfilesSnap] = await Promise.all([
          getDocs(collection(db, "reps")),
          getDocs(collection(db, "repProfiles")),
        ]);

        const repData = repsSnap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter(
            (rep) => rep.name && rep.name.trim().toLowerCase() !== "other"
          )
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        const profileMap = {};
        repProfilesSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          profileMap[docSnap.id] = {
            repCode: data.repCode || docSnap.id,
            displayName: (data.displayName || "").trim(),
            photoURL: data.photoURL || "",
            photoPath: data.photoPath || "",
          };
        });

        setReps(repData);
        setRepProfilesMap(profileMap);
      } catch (error) {
        console.error("Failed to load reps/profile names:", error);
      } finally {
        setLoadingReps(false);
      }
    };

    fetchInitialData();
  }, [siteUnlocked]);

  useEffect(() => {
    const loadProfileName = async () => {
      if (
        !selectedProfile ||
        selectedProfile.type !== "REP" ||
        !selectedProfile.code
      ) {
        setProfileName("");
        setDraftProfileName("");
        setProfilePhotoURL("");
        setProfilePhotoPath("");
        return;
      }

      try {
        const refDoc = doc(db, "repProfiles", selectedProfile.code);
        const snap = await getDoc(refDoc);

        if (snap.exists()) {
          const data = snap.data();
          const nextName = (data.displayName || "").trim();

          setProfileName(nextName);
          setDraftProfileName(nextName);
          setProfilePhotoURL(data.photoURL || "");
          setProfilePhotoPath(data.photoPath || "");

          setSelectedProfile((prev) =>
            prev && prev.type === "REP"
              ? {
                  ...prev,
                  displayName: nextName || prev.code,
                  photoURL: data.photoURL || "",
                }
              : prev
          );
        } else {
          setProfileName("");
          setDraftProfileName("");
          setProfilePhotoURL("");
          setProfilePhotoPath("");

          setSelectedProfile((prev) =>
            prev && prev.type === "REP"
              ? {
                  ...prev,
                  displayName: prev.code,
                  photoURL: "",
                }
              : prev
          );
        }
      } catch (error) {
        console.error("Failed to load rep profile:", error);
      }
    };

    loadProfileName();
  }, [selectedProfile?.code, selectedProfile?.type]);

  const handleSiteLogin = async (e) => {
    e.preventDefault();
    setSiteAuthLoading(true);
    setSiteAuthError("");

    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithEmailAndPassword(auth, SHARED_LOGIN_EMAIL, sitePassword);
      setSitePassword("");
    } catch (error) {
      console.error("Site login failed:", error);
      setSiteAuthError("Incorrect password");
    } finally {
      setSiteAuthLoading(false);
    }
  };

  const handleSiteLogout = async () => {
    try {
      await signOut(auth);
      setSelectedProfile(null);
      setActiveTab("EOD");
      setAdminUnlocked(false);
      setShowAdminLogin(false);
      setAdminPassword("");
      setAdminError("");
      setShowProfileModal(false);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleAdminLogin = () => {
    if (adminPassword === "AdminPassword") {
      setAdminUnlocked(true);
      setShowAdminLogin(false);
      setAdminPassword("");
      setAdminError("");
      setActiveTab("ADMIN");
    } else {
      setAdminError("Wrong password");
    }
  };

  const handleSelectRepProfile = (rep) => {
    const profile = repProfilesMap[rep.name];
    const displayName = profile?.displayName || rep.name;
    const photoURL = profile?.photoURL || "";

    setSelectedProfile({
      type: "REP",
      code: rep.name,
      label: rep.name,
      displayName,
      photoURL,
    });

    setActiveTab("EOD");
  };

  const handleSelectStoreProfile = () => {
    setSelectedProfile({
      type: "STORE",
      code: "STORE",
      label: "Store",
      displayName: "Store",
    });
    setActiveTab("EOD");
  };

  const handleSaveProfile = async () => {
    if (
      !selectedProfile ||
      selectedProfile.type !== "REP" ||
      !selectedProfile.code
    ) {
      setShowProfileModal(false);
      return;
    }

    setProfileSaving(true);

    try {
      const trimmed = draftProfileName.trim();

      await setDoc(
        doc(db, "repProfiles", selectedProfile.code),
        {
          repCode: selectedProfile.code,
          displayName: trimmed,
        },
        { merge: true }
      );

      setProfileName(trimmed);

      setRepProfilesMap((prev) => ({
        ...prev,
        [selectedProfile.code]: {
          ...(prev[selectedProfile.code] || {
            repCode: selectedProfile.code,
          }),
          displayName: trimmed,
          photoURL: prev[selectedProfile.code]?.photoURL || "",
          photoPath: prev[selectedProfile.code]?.photoPath || "",
        },
      }));

      setSelectedProfile((prev) =>
        prev && prev.type === "REP"
          ? {
              ...prev,
              displayName: trimmed || prev.code,
            }
          : prev
      );

      setShowProfileModal(false);
    } catch (error) {
      console.error("Failed to save profile:", error);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleUploadProfilePhoto = async (file) => {
    if (
      !selectedProfile ||
      selectedProfile.type !== "REP" ||
      !selectedProfile.code
    ) {
      return;
    }
    if (!file) return;

    setProfileUploading(true);

    try {
      if (profilePhotoPath) {
        try {
          await deleteObject(ref(storage, profilePhotoPath));
        } catch (err) {
          console.warn("Failed to delete old profile photo:", err);
        }
      }

      const ext = file.name.split(".").pop() || "jpg";
      const newPath = `repAvatars/${selectedProfile.code}-${Date.now()}.${ext}`;
      const storageRef = ref(storage, newPath);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await setDoc(
        doc(db, "repProfiles", selectedProfile.code),
        {
          repCode: selectedProfile.code,
          photoURL: url,
          photoPath: newPath,
        },
        { merge: true }
      );

      setProfilePhotoURL(url);
      setProfilePhotoPath(newPath);

      setRepProfilesMap((prev) => ({
        ...prev,
        [selectedProfile.code]: {
          ...(prev[selectedProfile.code] || {
            repCode: selectedProfile.code,
          }),
          displayName:
            prev[selectedProfile.code]?.displayName ||
            selectedProfile.displayName ||
            selectedProfile.code,
          photoURL: url,
          photoPath: newPath,
        },
      }));

      setSelectedProfile((prev) =>
        prev && prev.type === "REP"
          ? {
              ...prev,
              photoURL: url,
            }
          : prev
      );
    } catch (err) {
      console.error("Failed to upload profile photo:", err);
    } finally {
      setProfileUploading(false);
    }
  };

  const handleDeleteProfilePhoto = async () => {
    if (
      !selectedProfile ||
      selectedProfile.type !== "REP" ||
      !selectedProfile.code
    ) {
      return;
    }

    setProfileUploading(true);

    try {
      if (profilePhotoPath) {
        try {
          await deleteObject(ref(storage, profilePhotoPath));
        } catch (err) {
          console.warn("Failed to delete profile photo:", err);
        }
      }

      await setDoc(
        doc(db, "repProfiles", selectedProfile.code),
        {
          photoURL: "",
          photoPath: "",
        },
        { merge: true }
      );

      setProfilePhotoURL("");
      setProfilePhotoPath("");

      setRepProfilesMap((prev) => ({
        ...prev,
        [selectedProfile.code]: {
          ...(prev[selectedProfile.code] || {
            repCode: selectedProfile.code,
          }),
          displayName:
            prev[selectedProfile.code]?.displayName ||
            selectedProfile.displayName ||
            selectedProfile.code,
          photoURL: "",
          photoPath: "",
        },
      }));

      setSelectedProfile((prev) =>
        prev && prev.type === "REP"
          ? {
              ...prev,
              photoURL: "",
            }
          : prev
      );
    } catch (err) {
      console.error("Failed to delete profile photo:", err);
    } finally {
      setProfileUploading(false);
    }
  };

  const isStoreProfile = selectedProfile?.type === "STORE";
  const selectedRepCode =
    selectedProfile?.type === "REP" ? selectedProfile.code : null;

  const visibleProfileName =
    selectedProfile?.type === "REP"
      ? selectedProfile.displayName || selectedProfile.code
      : selectedProfile?.label || "Store";

  const visibleProfilePhoto =
    selectedProfile?.type === "REP" ? selectedProfile.photoURL || "" : "";

  const ProfileIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c1.8-4 5-6 8-6s6.2 2 8 6" />
    </svg>
  );

  const GearIcon = () => (
    <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>
      ⚙
    </span>
  );

  const BackIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );

  const handleBackToLogin = () => {
    setShowProfileModal(false);
    setShowAdminLogin(false);
    setActiveTab("EOD");
    setSelectedProfile(null);
  };

  if (!authReady) {
    return (
      <div className="app">
        <div className="page-content" style={{ color: "#fff", textAlign: "center", paddingTop: 100 }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!siteUnlocked) {
    return (
      <div className="app">
        <div className="video-bg-wrap">
          <video className="video-bg" autoPlay muted loop playsInline>
            <source
              src={`${process.env.PUBLIC_URL}/videos/bg.mp4`}
              type="video/mp4"
            />
          </video>
          <div className="video-overlay"></div>
        </div>

        <div className="page-content">
          <div className="admin-card" style={{ maxWidth: 520, margin: "100px auto" }}>
            <div className="admin-header-card" style={{ marginBottom: 20 }}>
              <h2 className="admin-section-title">Site Access</h2>
              <p className="admin-section-subtext">
                Enter the site password to continue.
              </p>
            </div>

            <form onSubmit={handleSiteLogin}>
              <input
                type="password"
                className="admin-modal-input"
                placeholder="Enter site password"
                value={sitePassword}
                onChange={(e) => {
                  setSitePassword(e.target.value);
                  setSiteAuthError("");
                }}
              />

              {siteAuthError && (
                <div className="admin-modal-error" style={{ marginTop: 12 }}>
                  {siteAuthError}
                </div>
              )}

              <div className="admin-modal-actions site-access-actions" style={{ marginTop: 16 }}>
                <button
                  type="submit"
                  className="display-btn site-access-btn"
                  disabled={siteAuthLoading}
                >
                  {siteAuthLoading ? "Unlocking..." : "Unlock"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <footer className="tk-footer">
          <img
            src="/images/footer.png"
            alt="Powered by ThunderKick Apps"
            className="tk-footer-logo"
          />
        </footer>
      </div>
    );
  }

  if (!selectedProfile) {
    return (
      <div className="app">
        <div className="video-bg-wrap">
          <video className="video-bg" autoPlay muted loop playsInline>
            <source
              src={`${process.env.PUBLIC_URL}/videos/bg.mp4`}
              type="video/mp4"
            />
          </video>
          <div className="video-overlay"></div>
        </div>

        <div className="page-content">
          <div
            className="admin-card"
            style={{ maxWidth: 720, margin: "80px auto" }}
          >
            <div className="admin-header-card" style={{ marginBottom: 20 }}>
              <h2 className="admin-section-title">Select Profile</h2>
              <p className="admin-section-subtext">
                Choose your rep profile to log under your own code, or choose
                Store to submit for the whole store.
              </p>
            </div>

            <div className="admin-list">
              {loadingReps ? (
                <div className="admin-empty">Loading reps...</div>
              ) : (
                <>
                  {reps.map((rep) => {
                    const profile = repProfilesMap[rep.name];
                    const displayName = profile?.displayName || rep.name;
                    const avatarURL = profile?.photoURL || "";
                    const initial = displayName.charAt(0).toUpperCase();

                    return (
                      <button
                        key={rep.id}
                        className="admin-list-row"
                        style={{
                          width: "100%",
                          textAlign: "left",
                          background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          cursor: "pointer",
                        }}
                        onClick={() => handleSelectRepProfile(rep)}
                      >
                        <div className="admin-list-main">
                          <div className="admin-list-title-row">
                            <div className="rep-avatar-small">
                              {avatarURL ? (
                                <img src={avatarURL} alt={displayName} />
                              ) : (
                                <span>{initial}</span>
                              )}
                            </div>
                            <div className="admin-list-title">
                              {displayName}
                            </div>
                          </div>
                          <div className="admin-list-meta"></div>
                        </div>
                      </button>
                    );
                  })}

                  <button
                    className="admin-list-row"
                    style={{
                      width: "100%",
                      textAlign: "left",
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      cursor: "pointer",
                    }}
                    onClick={handleSelectStoreProfile}
                  >
                    <div className="admin-list-main">
                      <div className="admin-list-title">Store</div>
                      <div className="admin-list-meta">
                        Submit and view everything for the whole store
                      </div>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <footer className="tk-footer">
          <img
            src="/images/footer.png"
            alt="Powered by ThunderKick Apps"
            className="tk-footer-logo"
          />
        </footer>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="video-bg-wrap">
        <video className="video-bg" autoPlay muted loop playsInline>
          <source
            src={`${process.env.PUBLIC_URL}/videos/bg.mp4`}
            type="video/mp4"
          />
        </video>
        <div className="video-overlay"></div>
      </div>

      <div className="top-tabs">
        <button
          className={`top-tab ${activeTab === "EOD" ? "active" : ""}`}
          onClick={() => setActiveTab("EOD")}
        >
          EOD
        </button>

        <button
          className={`top-tab ${activeTab === "Reports" ? "active" : ""}`}
          onClick={() => setActiveTab("Reports")}
        >
          Reports
        </button>

        <button
          className={`top-tab ${activeTab === "Attach" ? "active" : ""}`}
          onClick={() => setActiveTab("Attach")}
        >
          Attach
        </button>

        <button
          className={`top-tab ${activeTab === "Charts" ? "active" : ""}`}
          onClick={() => setActiveTab("Charts")}
        >
          Charts
        </button>
      </div>

      <div
        className="admin-card"
        style={{
          maxWidth: 1100,
          margin: "18px auto 0",
          padding: "12px 16px",
        }}
      >
        <div className="admin-logged-in-row">
          {visibleProfilePhoto && (
            <div className="rep-avatar-small" style={{ marginRight: 10 }}>
              <img src={visibleProfilePhoto} alt={visibleProfileName} />
            </div>
          )}
          <div className="admin-list-meta">
            Logged in as: <strong>{visibleProfileName}</strong>
            {isStoreProfile
              ? " — Store mode"
              : ` — Rep mode (${selectedRepCode})`}
          </div>
        </div>
      </div>

      <div className="page-content">
        {activeTab === "EOD" && (
          <EODLogger
            selectedProfile={selectedProfile}
            lockedRepCode={selectedRepCode}
            isStoreMode={isStoreProfile}
          />
        )}

        {activeTab === "Reports" && (
          <Reports
            selectedProfile={selectedProfile}
            lockedRepCode={selectedRepCode}
            isStoreMode={isStoreProfile}
          />
        )}

        {activeTab === "Attach" && (
          <Attach
            selectedProfile={selectedProfile}
            lockedRepCode={selectedRepCode}
            isStoreMode={isStoreProfile}
          />
        )}

        {activeTab === "Charts" && (
          <Charts
            selectedProfile={selectedProfile}
            lockedRepCode={selectedRepCode}
            isStoreMode={isStoreProfile}
          />
        )}

        {activeTab === "ADMIN" && adminUnlocked && <Admin />}
      </div>

      <button
        className="fab back-fab"
        onClick={handleBackToLogin}
        aria-label="Back to login"
        title="Back to login"
      >
        <BackIcon />
      </button>

      <button
        className="fab profile-fab"
        onClick={() => {
          setDraftProfileName(profileName);
          setShowProfileModal(true);
        }}
        aria-label="Open profile"
        title="Profile"
      >
        <ProfileIcon />
      </button>

      <button
        className="fab admin-fab"
        onClick={() => {
          if (adminUnlocked) {
            setActiveTab("ADMIN");
          } else {
            setShowAdminLogin(true);
          }
        }}
        aria-label="Open admin"
        title="Admin"
      >
        <GearIcon />
      </button>

      {showProfileModal && (
        <div
          className="admin-modal-backdrop"
          onClick={() => setShowProfileModal(false)}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-title">Profile Setup</div>

            <div className="profile-photo-section">
              <div className="profile-photo-preview">
                {profilePhotoURL ? (
                  <img
                    src={profilePhotoURL}
                    alt="Profile"
                    className="profile-photo-img"
                  />
                ) : (
                  <div className="profile-photo-placeholder">
                    <span>
                      {(selectedProfile?.code || "?")
                        .charAt(0)
                        .toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              <div className="profile-photo-actions">
                <label className="display-btn profile-photo-upload-label">
                  {profileUploading ? "Uploading..." : "Upload Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleUploadProfilePhoto(file);
                        e.target.value = "";
                      }
                    }}
                    disabled={profileUploading}
                  />
                </label>

                {profilePhotoURL && (
                  <button
                    type="button"
                    className="secondary-btn profile-photo-delete-btn"
                    onClick={handleDeleteProfilePhoto}
                    disabled={profileUploading}
                  >
                    Remove Photo
                  </button>
                )}
              </div>
            </div>

            <input
              type="text"
              className="admin-modal-input"
              placeholder="Enter your display name"
              value={draftProfileName}
              onChange={(e) => setDraftProfileName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveProfile();
              }}
            />

            <div className="admin-section-subtext" style={{ marginTop: 10 }}>
              This changes the name shown on the login page and in Reports.
              Your rep code stays the same.
            </div>

            <div className="admin-modal-actions">
              <button
                className="secondary-btn"
                onClick={() => setShowProfileModal(false)}
              >
                Cancel
              </button>

              <button
                className="display-btn"
                onClick={handleSaveProfile}
                disabled={profileSaving}
              >
                {profileSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdminLogin && (
        <div
          className="admin-modal-backdrop"
          onClick={() => {
            setShowAdminLogin(false);
            setAdminPassword("");
            setAdminError("");
          }}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-title">Admin Access</div>

            <input
              type="password"
              className="admin-modal-input"
              placeholder="Enter password"
              value={adminPassword}
              onChange={(e) => {
                setAdminPassword(e.target.value);
                setAdminError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdminLogin();
              }}
            />

            {adminError && (
              <div className="admin-modal-error">{adminError}</div>
            )}

            <div className="admin-modal-actions">
              <button
                className="secondary-btn"
                onClick={() => {
                  setShowAdminLogin(false);
                  setAdminPassword("");
                  setAdminError("");
                }}
              >
                Cancel
              </button>

              <button className="display-btn" onClick={handleAdminLogin}>
                Unlock
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="tk-footer">
        <img
          src="/images/footer.png"
          alt="Powered by ThunderKick Apps"
          className="tk-footer-logo"
        />
      </footer>
    </div>
  );
}